import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { appDate, isTimeZone, viewerTimeZone } from "../src/app-dates.ts";
import { MAX_HORIZON_DAYS_LIMIT, MIN_NOTICE_HOURS_LIMIT } from "../src/faq-catalog.ts";
import { FAQ_PASTE_ROW_LIMIT, FAQ_QUESTION_LIMIT, parsePastedFaq } from "../src/faq-paste.ts";
import { TimeField } from "./date-time-fields.tsx";
import { useInboxAccess } from "./inbox-v2/use-inbox.ts";
import { TimeZoneField } from "./time-zone-field.tsx";
import { conversationPath } from "./workspace-context.tsx";

/*
 * The client's standing answers: what this workspace knows, answered once instead of asked every
 * time a conversation needs it (`apps/rep-workspace/src/faq-store.ts`,
 * `docs/2026-09-20-faq-and-thread-extraction-design.md`).
 *
 * The section opens with what the agent actually stopped for, because that is the only part a rep
 * can act on without first deciding what a FAQ should contain. Everything below it answers one of
 * those stops. A confirmed answer reaches the next run of the campaign it scopes, never the draft
 * on screen, and the copy says so rather than implying an edit takes effect now.
 *
 * Every form opens where it was asked for, under the row or button that opened it, the way the
 * remove panel always did. Until 2026-09-25 the answer form rendered once at the foot of the page,
 * a screen or two below the button, with focus left where it was, so "Answer this" looked like it
 * did nothing (Dawid, about 00:02 CEST: "answer this buttons are not working").
 */

const ShapeSchema = z.enum(["text", "number", "qa", "guard", "resource", "scheduling"]);
const KeySchema = z.object({
  key: z.string(),
  label: z.string(),
  hint: z.string(),
  policy: z.enum(["atomic", "validated", "expandable"]),
  campaignOverridable: z.boolean(),
  answerShape: ShapeSchema,
});
const EntrySchema = z.object({
  id: z.string(),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  contextKey: z.string(),
  label: z.string(),
  question: z.string().nullable(),
  answer: z.unknown(),
  confirmedBy: z.string(),
  confirmedByName: z.string(),
  confirmedAt: z.string(),
  ageing: z.boolean(),
});
const QuoteSchema = z.union([
  z.object({
    available: z.literal(true),
    quote: z.string(),
    conversationId: z.string(),
    campaignId: z.string(),
    subject: z.string(),
    bisonUrl: z.string().nullable(),
  }),
  z.object({
    available: z.literal(false),
    reason: z.enum(["no_source", "source_gone", "message_gone", "quote_moved"]),
  }),
]);
const ProposalSchema = z.object({
  id: z.string(),
  contextKey: z.string(),
  label: z.string(),
  question: z.string().nullable(),
  suggestedKey: z.string().nullable(),
  sourcePlanId: z.string().nullable(),
  decision: z
    .object({
      outcome: z.enum(["answers", "partially_answers", "not_an_answer"]),
      confidence: z.enum(["high", "medium", "low"]),
    })
    .nullable(),
  quote: QuoteSchema,
});
const FollowUpSchema = z.object({
  key: z.string(),
  label: z.string(),
  stops: z.number(),
  waiting: z.number(),
  waitingConversations: z.array(
    z.object({
      planId: z.string(),
      leadFirstName: z.string().nullable(),
      leadCompany: z.string().nullable(),
    }),
  ),
  standingImpossible: z.string().nullable(),
  answered: z.enum(["workspace", "campaign"]).nullable(),
  answeredIn: z.array(z.object({ id: z.string(), name: z.string() })),
});
const OverviewSchema = z.object({
  workspaceId: z.string(),
  entries: z.array(EntrySchema),
  proposals: z.array(ProposalSchema),
  campaigns: z.array(z.object({ id: z.string(), name: z.string() })),
  keys: z.array(KeySchema),
  followUp: z.array(FollowUpSchema),
  ageingAfterDays: z.number(),
});
type Overview = z.infer<typeof OverviewSchema>;
type FactKey = z.infer<typeof KeySchema>;
type Entry = z.infer<typeof EntrySchema>;
type Proposal = z.infer<typeof ProposalSchema>;
type FollowUpFact = z.infer<typeof FollowUpSchema>;
type Shape = z.infer<typeof ShapeSchema>;

export const faqPath = (workspaceId: string): string =>
  `/api/work/workspaces/${encodeURIComponent(workspaceId)}/faq`;

/** A row's own id, made here so a repeated submit revises its row instead of adding another. */
function newEntryId(): string {
  return `faq_${crypto.randomUUID().replaceAll("-", "")}`;
}

/**
 * What a save the server could not take says when nothing more is known, on Standing answers and on
 * a question card alike. It claims only what holds on both: the form is still open with what was
 * typed, and whether anything reached the server is not known.
 */
export const SAVE_REFUSED = "That was not saved. Try again.";

const ErrorSchema = z.object({ error: z.string(), message: z.string().optional() });

/** What went wrong, said as the thing the rep was trying to do rather than as a code. */
function failure(status: number, body: unknown): string {
  const parsed = ErrorSchema.safeParse(body);
  if (status === 401) return "Your session ended. Sign in again.";
  if (!parsed.success) return SAVE_REFUSED;
  if (parsed.data.error === "personal_data_refused")
    return (
      parsed.data.message ??
      "A standing answer may not carry someone's contact details: it would be offered to every later lead."
    );
  if (parsed.data.error === "supersede_not_acknowledged")
    return "Someone changed this fact's answers while you were writing. Reload the page, then save again.";
  if (parsed.data.error === "source_unavailable")
    return "The sentence this suggests can no longer be read from its conversation, so it cannot become an answer.";
  if (parsed.data.error === "proposal_already_decided") return "Someone already decided this one.";
  return parsed.data.message ?? SAVE_REFUSED;
}

async function send(path: string, body: unknown): Promise<void> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok)
    throw new Error(failure(response.status, await response.json().catch(() => null)));
}

/**
 * A stored answer, read rather than asserted.
 *
 * The value's shape is its key's business and this component shows several keys at once, so it
 * parses what arrived instead of claiming to know it. Every field is optional because each shape
 * carries a different few, and a value this does not recognise is shown as itself.
 */
const StoredAnswerSchema = z.union([
  z.string(),
  z.number(),
  z.looseObject({
    answer: z.string().optional(),
    asked_about: z.string().optional(),
    avoid: z.string().optional(),
    note: z.string().optional(),
    url: z.string().optional(),
    label: z.string().optional(),
    share: z.string().optional(),
    host_timezone: z.string().optional(),
    working_days: z.array(z.number()).optional(),
    start_time: z.string().optional(),
    end_time: z.string().optional(),
    buffer_before_minutes: z.number().optional(),
    buffer_after_minutes: z.number().optional(),
    min_notice_hours: z.number().optional(),
    max_horizon_days: z.number().optional(),
  }),
]);

const WEEKDAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
] as const;

/** The days a booking policy names, in week order, as a person writes them. */
function dayList(days: readonly number[]): string {
  return WEEKDAYS.filter((day) => days.includes(day.value))
    .map((day) => day.label)
    .join(", ");
}

/** The stored value as a person reads it, whatever shape its key happens to hold. */
export function readable(answer: unknown): string {
  const parsed = StoredAnswerSchema.safeParse(answer);
  if (!parsed.success) return JSON.stringify(answer);
  if (typeof parsed.data === "string") return parsed.data;
  if (typeof parsed.data === "number") return `${parsed.data} minutes`;
  const record = parsed.data;
  if (record.answer !== undefined) return record.answer;
  if (record.avoid !== undefined) return record.avoid;
  if (record.url !== undefined) return `${record.label ?? record.url} — ${record.url}`;
  if (record.host_timezone !== undefined)
    return [
      `${dayList(record.working_days ?? [])}, ${record.start_time}–${record.end_time} ${record.host_timezone}`,
      `at least ${record.min_notice_hours} hours' notice`,
      `up to ${record.max_horizon_days} days ahead`,
    ].join(", ");
  return JSON.stringify(answer);
}

export interface Draft {
  text: string;
  askedAbout: string;
  note: string;
  url: string;
  label: string;
  share: "first_reply" | "on_request";
  hostTimezone: string;
  workingDays: number[];
  startTime: string;
  endTime: string;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  minNoticeHours: number;
  maxHorizonDays: number;
}

export function emptyDraft(): Draft {
  return {
    text: "",
    askedAbout: "",
    note: "",
    url: "",
    label: "",
    share: "on_request",
    hostTimezone: viewerTimeZone(),
    workingDays: [1, 2, 3, 4, 5],
    startTime: "09:00",
    endTime: "17:00",
    bufferBeforeMinutes: 0,
    bufferAfterMinutes: 0,
    minNoticeHours: 24,
    maxHorizonDays: 14,
  };
}

/** The fields the route expects for this key's own shape, built from what the rep typed. */
export function fieldsFor(shape: Shape, draft: Draft): unknown {
  if (shape === "text") return { shape, text: draft.text };
  if (shape === "number") return { shape, minutes: Number(draft.text.trim()) };
  if (shape === "qa") return { shape, askedAbout: draft.askedAbout, text: draft.text };
  if (shape === "guard")
    return { shape, avoid: draft.text, note: draft.note.trim() === "" ? null : draft.note };
  if (shape === "resource")
    return { shape, url: draft.url, label: draft.label, share: draft.share };
  return {
    shape,
    hostTimezone: draft.hostTimezone,
    workingDays: draft.workingDays,
    startTime: draft.startTime,
    endTime: draft.endTime,
    bufferBeforeMinutes: draft.bufferBeforeMinutes,
    bufferAfterMinutes: draft.bufferAfterMinutes,
    minNoticeHours: draft.minNoticeHours,
    maxHorizonDays: draft.maxHorizonDays,
  };
}

/**
 * Why the notice or horizon a rep typed is refused, or null when the route would take it. One
 * sentence per refusal, said under the box it is about and never beside Save as well. The limits
 * are the route's own (`StandingAnswerFieldsSchema`): a number it takes is never refused here, and
 * one it refuses is never offered to it.
 */
const HORIZON_UNDER = "Book at least one day ahead.";
const HORIZON_OVER = `Book at most ${MAX_HORIZON_DAYS_LIMIT} days ahead.`;
const NOTICE_OVER = `Ask for at most ${MIN_NOTICE_HOURS_LIMIT} hours' notice (${MIN_NOTICE_HOURS_LIMIT / 24} days).`;
const noticeProblem = (hours: number): string | null =>
  hours > MIN_NOTICE_HOURS_LIMIT ? NOTICE_OVER : null;
function horizonProblem(days: number): string | null {
  if (days < 1) return HORIZON_UNDER;
  return days > MAX_HORIZON_DAYS_LIMIT ? HORIZON_OVER : null;
}

/**
 * A whole number as typed into the notice and horizon boxes: digits only, and one digit more than
 * the limit has. Nothing is cut down to a number the route would take: what is kept of a longer
 * entry is over the limit, so "1000" stays 1000 and is refused in words rather than saved as 100.
 * Unbounded, a long paste read back as `1e+23` (seen 2026-10-13).
 */
function typedCount(text: string, limit: number): number {
  // The box shows 0 when emptied, so a leading zero is the box's own and not a digit typed.
  const digits = text.replace(/\D/gu, "").replace(/^0+/u, "");
  return Number(digits.slice(0, String(limit).length + 1));
}

/**
 * One of the two number boxes, which says under itself why its number is refused. A question card
 * turns Save off for it and has no line beside Save to say why (seen 2026-10-13).
 *
 * Both boxes are this one structure, so they sit level side by side. A reason that is true only
 * mid-edit waits until the rep leaves the box: emptied to type a new number, a box reads 0, and
 * saying so then would announce a refusal nobody made. A number over the limit is never a step on
 * the way to another, so that reason shows as typed.
 */
function CountBox(props: {
  id: string;
  label: string;
  value: number;
  limit: number;
  problem: string | null;
  /** Whether the reason waits for the rep to leave the box. */
  waits: boolean;
  /** Save was pressed and refused: the reason is said now, wherever the caret is. */
  pressed: boolean;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  // A value the form opened with is not being edited, so its reason shows from the start.
  const [left, setLeft] = useState(true);
  const said = props.problem !== null && (!props.waits || left || props.pressed);
  return (
    <div className="lcr-faq-field">
      <label htmlFor={props.id}>{props.label}</label>
      <input
        id={props.id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className="lcr-faq-narrow"
        value={String(props.value)}
        disabled={props.disabled}
        aria-invalid={said ? true : undefined}
        aria-describedby={said ? `${props.id}-problem` : undefined}
        onChange={(event) => {
          setLeft(false);
          props.onChange(typedCount(event.target.value, props.limit));
        }}
        onBlur={() => setLeft(true)}
      />
      {said && (
        <p className="lcr-entry-problem" id={`${props.id}-problem`} role="alert">
          {props.problem}
        </p>
      )}
    </div>
  );
}

/** The notice and horizon boxes, side by side: one structure, so they sit level. */
function NoticeAndHorizon(props: {
  idPrefix: string;
  draft: Draft;
  disabled: boolean;
  pressed: boolean;
  onChange: (next: Partial<Draft>) => void;
}) {
  const { idPrefix, draft, disabled, pressed, onChange } = props;
  return (
    <div className="lcr-faq-pair">
      <CountBox
        id={`${idPrefix}-notice`}
        label="Shortest notice, in hours"
        value={draft.minNoticeHours}
        limit={MIN_NOTICE_HOURS_LIMIT}
        problem={noticeProblem(draft.minNoticeHours)}
        waits={false}
        pressed={pressed}
        disabled={disabled}
        onChange={(minNoticeHours) => onChange({ minNoticeHours })}
      />
      <CountBox
        id={`${idPrefix}-horizon`}
        label="How far ahead, in days"
        value={draft.maxHorizonDays}
        limit={MAX_HORIZON_DAYS_LIMIT}
        problem={horizonProblem(draft.maxHorizonDays)}
        waits={draft.maxHorizonDays < 1}
        pressed={pressed}
        disabled={disabled}
        onChange={(maxHorizonDays) => onChange({ maxHorizonDays })}
      />
    </div>
  );
}

/**
 * Why Save will not save, beside it. Said once: a reason a number box says under itself stays here
 * only as Save's description, for a screen reader, and is not written out a second time.
 */
function SaveReason(props: {
  id: string;
  reason: string | null;
  /** What the last press of Save was told, by the form or by the server. */
  message: string;
  seen: boolean;
  inBox: boolean;
}) {
  if (props.message && !props.inBox) return <span role="alert">{props.message}</span>;
  if (props.reason === null) return null;
  return (
    <span
      id={props.id}
      className={props.seen && !props.inBox ? "lcr-faq-missing" : "lcr-visually-hidden"}
    >
      {props.reason}
    </span>
  );
}

/** Whether the reason Save would give is one a number box says under itself. */
function saidUnderItsBox(shape: Shape, draft: Draft, reason: string | null): boolean {
  return (
    shape === "scheduling" &&
    reason !== null &&
    (reason === noticeProblem(draft.minNoticeHours) ||
      reason === horizonProblem(draft.maxHorizonDays))
  );
}

/**
 * What is still missing before this answer can be saved, or null when nothing is.
 *
 * Said next to a disabled Save, because a button that will not press and does not say why reads as
 * broken. Each check is one the host makes too (`StandingAnswerFieldsSchema`), so the rep hears it
 * here rather than as a refusal after posting.
 */
export function missing(shape: Shape, draft: Draft): string | null {
  if (shape === "number") {
    const minutes = draft.text.trim();
    if (!/^\d{1,3}$/u.test(minutes) || Number(minutes) < 1 || Number(minutes) > 480)
      return "Enter the length in whole minutes, from 1 to 480.";
    return null;
  }
  if (shape === "qa") {
    if (draft.askedAbout.trim() === "") return "Say what leads ask.";
    if (draft.text.trim() === "") return "Write the answer.";
    return null;
  }
  if (shape === "resource") {
    if (!draft.url.startsWith("https://")) return "Add a link that starts with https://.";
    if (draft.label.trim() === "") return "Say what the link is.";
    return null;
  }
  if (shape === "scheduling") {
    if (!isTimeZone(draft.hostTimezone.trim()))
      return "Choose the timezone these hours are in, as a region and city such as Europe/Warsaw.";
    if (draft.workingDays.length === 0) return "Choose at least one day.";
    // A time field hands over nothing for an entry it refused, an Until before its From included.
    if (draft.startTime === "") return "Enter the time these hours start, like 09:00.";
    if (draft.endTime === "") return "Enter the time these hours end, after they start.";
    if (draft.startTime >= draft.endTime) return "The hours must end after they start.";
    return noticeProblem(draft.minNoticeHours) ?? horizonProblem(draft.maxHorizonDays);
  }
  if (draft.text.trim() === "")
    return shape === "guard" ? "Say what a reply must never say." : "Write the answer.";
  return null;
}

export function complete(shape: Shape, draft: Draft): boolean {
  return missing(shape, draft) === null;
}

/**
 * The fields for one fact, which are its key's and not a single box for everything.
 *
 * A link needs its address and what it is; a booking policy needs hours and a horizon. Collapsing
 * them into prose is what makes a FAQ a paragraph nobody can scope, which is the shape this design
 * exists to avoid.
 */
export function AnswerFields(props: {
  shape: Shape;
  draft: Draft;
  disabled: boolean;
  onChange: (next: Partial<Draft>) => void;
  idPrefix: string;
  /** Save was pressed and refused, on a form whose Save can be pressed while something is missing. */
  pressed?: boolean | undefined;
}) {
  const { shape, draft, disabled, onChange, idPrefix } = props;
  if (shape === "number")
    return (
      <label className="lcr-faq-field" htmlFor={`${idPrefix}-minutes`}>
        <span>Minutes</span>
        <input
          id={`${idPrefix}-minutes`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="lcr-faq-narrow"
          value={draft.text}
          disabled={disabled}
          onChange={(event) => onChange({ text: event.target.value })}
        />
      </label>
    );
  if (shape === "resource")
    return (
      <>
        <label className="lcr-faq-field" htmlFor={`${idPrefix}-url`}>
          <span>Link</span>
          <input
            id={`${idPrefix}-url`}
            type="url"
            value={draft.url}
            disabled={disabled}
            placeholder="https://"
            onChange={(event) => onChange({ url: event.target.value })}
          />
        </label>
        <label className="lcr-faq-field" htmlFor={`${idPrefix}-label`}>
          <span>What it is, in your words</span>
          <input
            id={`${idPrefix}-label`}
            type="text"
            value={draft.label}
            disabled={disabled}
            placeholder="For example: a three-minute walkthrough"
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </label>
        <label className="lcr-faq-field" htmlFor={`${idPrefix}-share`}>
          <span>When to send it</span>
          <span className="lcr-select">
            <select
              id={`${idPrefix}-share`}
              value={draft.share}
              disabled={disabled}
              onChange={(event) =>
                onChange({
                  share: event.target.value === "first_reply" ? "first_reply" : "on_request",
                })
              }
            >
              <option value="on_request">Only when a lead asks for material</option>
              <option value="first_reply">Once, in the reply that arranges the call</option>
            </select>
          </span>
        </label>
      </>
    );
  if (shape === "scheduling") {
    const here = viewerTimeZone();
    return (
      <>
        <div className="lcr-faq-field">
          <label htmlFor={`${idPrefix}-tz`}>Timezone these hours are in</label>
          <TimeZoneField
            id={`${idPrefix}-tz`}
            value={draft.hostTimezone}
            disabled={disabled}
            onChange={(hostTimezone) => onChange({ hostTimezone })}
          />
          {draft.hostTimezone.trim() !== here && (
            <button
              type="button"
              className="lcr-linklike lcr-faq-suggest"
              disabled={disabled}
              onClick={() => {
                onChange({ hostTimezone: here });
                // The suggestion goes once it is taken, so focus moves to the field it filled.
                document.getElementById(`${idPrefix}-tz`)?.focus();
              }}
            >
              Use this browser's timezone, {here}
            </button>
          )}
        </div>
        <fieldset className="lcr-faq-days">
          <legend>Days you take calls</legend>
          {WEEKDAYS.map((day) => (
            <label key={day.value} className="lcr-faq-day">
              <input
                type="checkbox"
                checked={draft.workingDays.includes(day.value)}
                disabled={disabled}
                onChange={(event) =>
                  onChange({
                    workingDays: event.target.checked
                      ? [...draft.workingDays, day.value]
                      : draft.workingDays.filter((value) => value !== day.value),
                  })
                }
              />
              <span>{day.label}</span>
            </label>
          ))}
        </fieldset>
        <div className="lcr-faq-pair">
          <div className="lcr-faq-field">
            <label htmlFor={`${idPrefix}-start`}>From</label>
            <TimeField
              id={`${idPrefix}-start`}
              value={draft.startTime}
              disabled={disabled}
              onChange={(startTime) => onChange({ startTime })}
            />
          </div>
          <div className="lcr-faq-field">
            <label htmlFor={`${idPrefix}-end`}>Until</label>
            <TimeField
              id={`${idPrefix}-end`}
              value={draft.endTime}
              disabled={disabled}
              after={
                draft.startTime === ""
                  ? undefined
                  : { value: draft.startTime, message: "The hours must end after they start." }
              }
              onChange={(endTime) => onChange({ endTime })}
            />
          </div>
        </div>
        <NoticeAndHorizon
          idPrefix={idPrefix}
          draft={draft}
          disabled={disabled}
          pressed={props.pressed === true}
          onChange={onChange}
        />
      </>
    );
  }
  return (
    <>
      {shape === "qa" && (
        <label className="lcr-faq-field" htmlFor={`${idPrefix}-asked`}>
          <span>What leads ask</span>
          <input
            id={`${idPrefix}-asked`}
            type="text"
            maxLength={FAQ_QUESTION_LIMIT}
            value={draft.askedAbout}
            disabled={disabled}
            placeholder="For example: do you work with teams our size?"
            onChange={(event) => onChange({ askedAbout: event.target.value })}
          />
        </label>
      )}
      <label className="lcr-faq-field" htmlFor={`${idPrefix}-text`}>
        <span>{shape === "guard" ? "What a reply must never say" : "Answer"}</span>
        <textarea
          id={`${idPrefix}-text`}
          value={draft.text}
          disabled={disabled}
          onChange={(event) => onChange({ text: event.target.value })}
        />
      </label>
      {shape === "guard" && (
        <label className="lcr-faq-field" htmlFor={`${idPrefix}-note`}>
          <span>Why, for your team (optional)</span>
          <input
            id={`${idPrefix}-note`}
            type="text"
            value={draft.note}
            disabled={disabled}
            onChange={(event) => onChange({ note: event.target.value })}
          />
        </label>
      )}
    </>
  );
}

/** A form's fields filled from an answer already held, for editing it. */
function draftFrom(answer: unknown, question: string | null, start: Draft): Draft {
  const parsed = StoredAnswerSchema.safeParse(answer);
  if (!parsed.success) return start;
  if (typeof parsed.data === "string") return { ...start, text: parsed.data };
  if (typeof parsed.data === "number") return { ...start, text: String(parsed.data) };
  const record = parsed.data;
  return {
    ...start,
    text: record.answer ?? record.avoid ?? "",
    askedAbout: record.asked_about ?? question ?? "",
    note: record.note ?? "",
    url: record.url ?? "",
    label: record.label ?? "",
    share: record.share === "first_reply" ? "first_reply" : "on_request",
    hostTimezone: record.host_timezone ?? start.hostTimezone,
    workingDays: record.working_days ?? start.workingDays,
    startTime: record.start_time ?? start.startTime,
    endTime: record.end_time ?? start.endTime,
    bufferBeforeMinutes: record.buffer_before_minutes ?? start.bufferBeforeMinutes,
    bufferAfterMinutes: record.buffer_after_minutes ?? start.bufferAfterMinutes,
    minNoticeHours: record.min_notice_hours ?? start.minNoticeHours,
    maxHorizonDays: record.max_horizon_days ?? start.maxHorizonDays,
  };
}

/**
 * The answer a save at this scope would replace, if any.
 *
 * Decided for the scope the rep has chosen, not the one the form opened at: a fact that holds one
 * value has one row per scope, and choosing a campaign that already holds one replaces that row. The
 * page once looked only at the scope it opened with, so saving to such a campaign was refused as if
 * someone else had changed it.
 */
function holderFor(
  fact: Pick<FactKey, "key" | "policy">,
  entries: readonly Pick<Entry, "id" | "contextKey" | "campaignId" | "answer">[],
  campaignId: string | null,
  editing: string | null,
) {
  if (fact.policy === "expandable") return null;
  return (
    entries.find(
      (entry) =>
        entry.contextKey === fact.key && entry.campaignId === campaignId && entry.id !== editing,
    ) ?? null
  );
}

/**
 * The campaigns' own answers an answer for every campaign leaves in place.
 *
 * Shown in the form and sent with the save, which keeps them current: a workspace answer newer than
 * a campaign's own makes the campaign's a `stale_shadow`, and its next reply would stop to ask which
 * one applies (`keepCampaignAnswers` in faq-store.ts).
 */
function keptFor(
  fact: Pick<FactKey, "key" | "campaignOverridable">,
  entries: readonly Entry[],
  campaignId: string | null,
  editing: string | null,
): Entry[] {
  if (campaignId !== null || !fact.campaignOverridable) return [];
  return entries.filter(
    (entry) => entry.contextKey === fact.key && entry.campaignId !== null && entry.id !== editing,
  );
}

/**
 * Writing or replacing one standing answer, opened in place.
 *
 * `supersedes` carries the value the rep is looking at when this key already holds one at the
 * chosen scope, which is how the host knows the replacement was decided with the old answer in view
 * rather than over it. The first field takes focus when it opens, and Escape closes it the way it
 * closes the remove panel.
 */
function AnswerForm(props: {
  workspaceId: string;
  fact: FactKey;
  campaigns: Overview["campaigns"];
  entries: readonly Entry[];
  /** The saved answer being edited, or null for a new one. */
  existing: Entry | null;
  /** A row picked from pasted text, to check before it is saved. */
  seed: { question: string; answer: string } | null;
  /** The campaign this answer should apply to, when another section sent the rep here to set it. */
  initialCampaignId: string | null;
  /** What the rep typed last time this form was open and left without saving or cancelling. */
  draft: Draft | null;
  /**
   * Whether the title shows. Under a stop's own row the row already names the fact directly above,
   * so the title stays only as the form's accessible name there.
   */
  titled: boolean;
  onDraft: (draft: Draft) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { fact, existing, seed } = props;
  const [draft, setDraft] = useState<Draft>(() => {
    if (props.draft) return props.draft;
    // The page asks for the booking timezone rather than assuming the browser's: the host's zone
    // labels every time a reply states, and a rep travelling would otherwise save where they are.
    const start = { ...emptyDraft(), hostTimezone: "" };
    if (existing) return draftFrom(existing.answer, existing.question, start);
    if (seed) return { ...start, askedAbout: seed.question, text: seed.answer };
    return start;
  });
  /** What the form opened with, so an edit that changed nothing does not offer to save. */
  const [initial] = useState(draft);
  /** Whether the rep has typed or tried to save, before which a missing field is not announced. */
  const [touched, setTouched] = useState(false);
  const [campaignId, setCampaignId] = useState<string>(
    existing?.campaignId ?? props.initialCampaignId ?? "",
  );
  const [entryId] = useState(() => existing?.id ?? newEntryId());
  const [message, setMessage] = useState("");
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const node = form.current;
    if (!node) return;
    node.scrollIntoView?.({ block: "nearest" });
    node.querySelector<HTMLElement>("input, textarea, select")?.focus({ preventScroll: true });
  }, []);
  const scope = campaignId === "" ? null : campaignId;
  const holder = holderFor(fact, props.entries, scope, existing?.id ?? null);
  const kept = keptFor(fact, props.entries, scope, existing?.id ?? null);
  const cache = useQueryClient();
  const save = useMutation({
    mutationFn: async () => {
      await send(`${faqPath(props.workspaceId)}/entries`, {
        entryId,
        contextKey: fact.key,
        campaignId: scope,
        fields: fieldsFor(fact.answerShape, draft),
        // Replacing a fact that holds one answer is a decision, so it carries what was on screen.
        ...(holder ? { supersedes: holder.answer } : {}),
        ...(scope === null && fact.campaignOverridable
          ? {
              keepCampaignAnswers: kept.map((entry) => ({
                entryId: entry.id,
                answer: entry.answer,
              })),
            }
          : {}),
      });
    },
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ["faq", props.workspaceId] });
      props.onDone();
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const idPrefix = `lcr-faq-${fact.key}`;
  const titleId = `${idPrefix}-form-title`;
  /*
   * Why Save will not save yet. Save stays focusable and names this as its description, because a
   * disabled button can be neither reached by keyboard nor asked why; pressing it says so aloud.
   */
  const reason =
    missing(fact.answerShape, draft) ??
    (existing && JSON.stringify(draft) === JSON.stringify(initial)
      ? "Change the answer to save it."
      : null);
  const inBox = saidUnderItsBox(fact.answerShape, draft, reason);
  const campaignName = (id: string | null) =>
    props.campaigns.find((campaign) => campaign.id === id)?.name ?? "one campaign";
  return (
    <form
      ref={form}
      id={`${idPrefix}-form`}
      className="lcr-faq-form"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !save.isPending) {
          event.stopPropagation();
          props.onCancel();
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (reason !== null) {
          setTouched(true);
          setMessage(reason);
          form.current?.querySelector<HTMLElement>("input, textarea, select")?.focus();
          return;
        }
        setMessage("");
        save.mutate();
      }}
    >
      <p id={titleId} className={props.titled ? "lcr-faq-form-title" : "lcr-visually-hidden"}>
        {existing ? `Edit: ${fact.label}` : fact.label}
      </p>
      <p className="lcr-faq-hint">{fact.hint}</p>
      {holder && (
        <p className="lcr-faq-replacing">
          Replaces the answer{" "}
          {scope === null ? "every campaign uses" : `${campaignName(scope)} has`} now:{" "}
          <q>{readable(holder.answer)}</q>
        </p>
      )}
      <AnswerFields
        shape={fact.answerShape}
        draft={draft}
        disabled={save.isPending}
        idPrefix={idPrefix}
        pressed={inBox && message !== ""}
        onChange={(next) => {
          const updated = { ...draft, ...next };
          setDraft(updated);
          setTouched(true);
          setMessage("");
          props.onDraft(updated);
        }}
      />
      {existing ? (
        <p className="lcr-faq-hint">
          Applies to{" "}
          {existing.campaignId === null
            ? "every campaign in this workspace"
            : `${campaignName(existing.campaignId)} only`}
          . To change where an answer applies, add one for the other place and remove this one.
        </p>
      ) : (
        fact.campaignOverridable &&
        props.campaigns.length > 0 && (
          <label className="lcr-faq-field" htmlFor={`${idPrefix}-scope`}>
            <span>Where it applies</span>
            <span className="lcr-select">
              <select
                id={`${idPrefix}-scope`}
                value={campaignId}
                disabled={save.isPending}
                onChange={(event) => setCampaignId(event.target.value)}
              >
                <option value="">
                  {keptFor(fact, props.entries, null, null).length > 0
                    ? "Every campaign without its own answer"
                    : "Every campaign in this workspace"}
                </option>
                {props.campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} only
                    {holderFor(fact, props.entries, campaign.id, null)
                      ? " (replaces its answer)"
                      : ""}
                  </option>
                ))}
              </select>
            </span>
          </label>
        )
      )}
      {kept.length > 0 && (
        <div className="lcr-faq-kept">
          <p className="lcr-faq-hint">
            {kept.length === 1
              ? "This campaign keeps its own answer"
              : "These campaigns keep their own answers"}
            . To use this one there too, remove theirs under Your answers.
          </p>
          <ul>
            {kept.map((entry) => (
              <li key={entry.id}>
                <strong className="lcr-faq-kept-name">
                  {entry.campaignName ?? campaignName(entry.campaignId)}
                </strong>
                : <q>{readable(entry.answer)}</q>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="lcr-actions">
        <button
          type="submit"
          className="lcr-small-button lcr-primary"
          disabled={save.isPending}
          aria-disabled={reason !== null}
          aria-describedby={reason !== null ? `${idPrefix}-missing` : undefined}
        >
          {save.isPending ? "Saving…" : existing ? "Save changes" : "Save answer"}
        </button>
        <button
          type="button"
          className="lcr-small-button"
          disabled={save.isPending}
          onClick={props.onCancel}
        >
          Cancel
        </button>
        <SaveReason
          id={`${idPrefix}-missing`}
          reason={reason}
          message={message}
          seen={touched}
          inBox={inBox}
        />
      </div>
    </form>
  );
}

/** "Spring outreach has its own answer", naming at most two campaigns before counting them. */
function ownAnswers(campaigns: readonly { name: string }[]): string {
  const [first, second] = campaigns;
  if (!first) return "";
  if (campaigns.length === 1) return `${first.name} has its own answer`;
  if (campaigns.length === 2 && second)
    return `${first.name} and ${second.name} have their own answers`;
  return `${campaigns.length} campaigns have their own answers`;
}

/** How a waiting conversation is named in its link: the lead as the inbox shows them. */
function leadName(conversation: FollowUpFact["waitingConversations"][number]): string {
  const parts = [conversation.leadFirstName, conversation.leadCompany].filter(
    (part): part is string => part !== null && part.trim() !== "",
  );
  return parts.length > 0 ? parts.join(", ") : "a conversation";
}

/**
 * What the agent stopped for in this workspace, most often first.
 *
 * Counted from this workspace's own conversations rather than offered as a blank form, because a
 * rep cannot guess which facts their campaigns need and the pipeline already knows. Facts no
 * standing answer could prevent stay in the list with the reason: leaving them out invites the
 * reasonable guess that the field was forgotten.
 *
 * A standing answer is read by replies drafted after it, so it does not release a conversation that
 * is already waiting. Each row links the ones waiting, because that is where they are answered.
 */
function FollowUp(props: {
  workspaceId: string;
  followUp: Overview["followUp"];
  keys: FactKey[];
  /** The fact whose form is open in this list, if any. */
  openKey: string | null;
  form: ReactNode;
  onAnswer: (key: string) => void;
}) {
  const inbox = useInboxAccess(props.workspaceId).data?.inbox === true;
  const standing = props.followUp.filter(
    (fact) => fact.standingImpossible === null && props.keys.some((key) => key.key === fact.key),
  );
  // What still needs the rep first, most frequent first; a fact answered for every campaign is
  // done, so it moves below the ones that are not.
  const answerable = [
    ...standing.filter((fact) => fact.answered !== "workspace"),
    ...standing.filter((fact) => fact.answered === "workspace"),
  ];
  const rest = props.followUp.filter((fact) => !standing.includes(fact));
  if (props.followUp.length === 0) return null;
  return (
    <div className="lcr-faq-followup">
      <h2>What has stopped a reply here</h2>
      <p className="lcr-settings-note">
        Answer a fact once and replies drafted after that use it. A conversation already waiting
        still needs its own answer, so each one is linked.
      </p>
      {answerable.length === 0 ? (
        <p className="lcr-settings-note">
          Every fact that stopped a reply here is one that changes per conversation, so no standing
          answer could have prevented it.
        </p>
      ) : (
        <ul className="lcr-faq-followup-list">
          {answerable.map((fact) => {
            const open = props.openKey === fact.key;
            const nameId = `lcr-faq-fact-${fact.key}-name`;
            return (
              <li key={fact.key} id={`lcr-faq-fact-${fact.key}`} tabIndex={-1}>
                <div className="lcr-faq-followup-text">
                  <span id={nameId} className="lcr-faq-followup-name">
                    {fact.label}
                  </span>
                  <span className="lcr-faq-followup-count">
                    Stopped {fact.stops} {fact.stops === 1 ? "conversation" : "conversations"}
                    {fact.answered === "campaign" && <> · {ownAnswers(fact.answeredIn)}</>}
                  </span>
                  {fact.waiting > 0 && (
                    <span className="lcr-faq-followup-waiting">
                      {fact.waiting === 1
                        ? "1 is still waiting for your answer:"
                        : `${fact.waiting} are still waiting for your answer:`}{" "}
                      {fact.waitingConversations.map((conversation, index) => (
                        <span key={conversation.planId}>
                          {index > 0 && ", "}
                          <a href={conversationPath(props.workspaceId, conversation.planId, inbox)}>
                            {leadName(conversation)}
                          </a>
                        </span>
                      ))}
                      {fact.waiting > fact.waitingConversations.length &&
                        ` and ${fact.waiting - fact.waitingConversations.length} more in the inbox`}
                    </span>
                  )}
                </div>
                {fact.answered === "workspace" ? (
                  <span className="lcr-chip lcr-chip-green">Answered</span>
                ) : (
                  <button
                    type="button"
                    className="lcr-small-button lcr-faq-answer-open"
                    aria-expanded={open}
                    aria-controls={open ? `lcr-faq-${fact.key}-form` : undefined}
                    aria-describedby={nameId}
                    onClick={() => props.onAnswer(fact.key)}
                  >
                    {fact.answered === "campaign" ? "Answer for other campaigns" : "Answer"}
                  </button>
                )}
                {open && <div className="lcr-faq-inline">{props.form}</div>}
              </li>
            );
          })}
        </ul>
      )}
      {rest.length > 0 && (
        <details className="lcr-faq-aside">
          <summary>
            {rest.length} {rest.length === 1 ? "fact" : "facts"} a standing answer cannot hold
          </summary>
          <ul className="lcr-faq-reasons">
            {rest.map((fact) => (
              <li key={fact.key}>
                <strong>{fact.label}</strong>
                <span>{fact.standingImpossible ?? "Answered per conversation."}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

const QUOTE_REASON: Readonly<Record<string, string>> = {
  no_source: "This came from pasted text, so there is no conversation to read it back from.",
  source_gone: "The conversation this came from is no longer here.",
  message_gone: "The message this came from is no longer in the thread.",
  quote_moved:
    "The sentence has changed since this was suggested, so it cannot be used as written.",
};

/**
 * Sentences from this workspace's own threads, waiting on a rep.
 *
 * Each quote is cut from its conversation as it stands right now rather than stored, so a thread
 * that has moved on says so instead of showing words the seller no longer wrote. Accepting one is
 * the only way an extracted sentence becomes an answer.
 */
function ProposalQueue(props: { workspaceId: string; proposals: Proposal[] }) {
  const cache = useQueryClient();
  const inbox = useInboxAccess(props.workspaceId).data?.inbox === true;
  const [message, setMessage] = useState("");
  const decide = useMutation({
    mutationFn: async (input: { id: string; decision: "accepted" | "rejected" }) => {
      await send(`${faqPath(props.workspaceId)}/proposals/${input.id}/decision`, {
        decision: input.decision,
      });
    },
    onSuccess: () => cache.invalidateQueries({ queryKey: ["faq", props.workspaceId] }),
    onError: (error: Error) => setMessage(error.message),
  });
  if (props.proposals.length === 0) return null;
  return (
    <div className="lcr-faq-proposals">
      <h2>Suggested from your own replies</h2>
      <p className="lcr-settings-note">
        Sentences your team already wrote to leads in this workspace. Nothing here is in use until
        you accept it.
      </p>
      <ul className="lcr-faq-proposal-list">
        {props.proposals.map((proposal) => {
          // Only the suggestion being decided waits; the others stay usable.
          const pending = decide.isPending && decide.variables?.id === proposal.id;
          return (
            <li key={proposal.id} className="lcr-faq-proposal">
              <p className="lcr-faq-proposal-question">{proposal.question ?? proposal.label}</p>
              {proposal.quote.available ? (
                <blockquote>{proposal.quote.quote}</blockquote>
              ) : (
                <p className="lcr-faq-proposal-gone" role="status">
                  {QUOTE_REASON[proposal.quote.reason] ?? "This sentence can no longer be read."}
                </p>
              )}
              <p className="lcr-faq-proposal-meta">
                {proposal.quote.available && proposal.sourcePlanId && (
                  <>
                    <a href={conversationPath(props.workspaceId, proposal.sourcePlanId, inbox)}>
                      {proposal.quote.subject}
                    </a>
                    {" · "}
                  </>
                )}
                {proposal.decision
                  ? `${
                      proposal.decision.outcome === "answers"
                        ? "Reads as an answer"
                        : proposal.decision.outcome === "partially_answers"
                          ? "Reads as a partial answer"
                          : "Read as not an answer"
                    }, ${proposal.decision.confidence} confidence`
                  : "No reading recorded"}
              </p>
              <div className="lcr-actions">
                <button
                  type="button"
                  className="lcr-small-button lcr-faq-accept"
                  disabled={pending || !proposal.quote.available}
                  onClick={() => decide.mutate({ id: proposal.id, decision: "accepted" })}
                >
                  Use this answer
                </button>
                <button
                  type="button"
                  className="lcr-small-button"
                  disabled={pending}
                  onClick={() => decide.mutate({ id: proposal.id, decision: "rejected" })}
                >
                  Not this
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {message && <p role="alert">{message}</p>}
    </div>
  );
}

/**
 * A block of existing FAQ text, split into rows the rep saves one at a time.
 *
 * It proposes boundaries rather than storing the prose: a row has a question, an answer and a
 * scope, and a pasted paragraph has none of the three. What it cannot split it hands back rather
 * than inventing a question for it. It stays mounted while a row is being checked, so the pasted
 * text and the other rows are still there afterwards, and a saved row says so.
 */
function PasteBlock(props: {
  saved: ReadonlySet<string>;
  checking: string | null;
  /** The check form for the row being checked, rendered under that row. */
  form: ReactNode;
  onPicked: (row: { question: string; answer: string }) => void;
}) {
  const [text, setText] = useState("");
  const parsed = text.trim().length === 0 ? null : parsePastedFaq(text);
  return (
    <details className="lcr-faq-paste">
      <summary>Paste a block of FAQ you already have</summary>
      <p className="lcr-settings-note">
        It splits into rows you check one at a time. Nothing is saved until you save each row.
      </p>
      <label className="lcr-faq-field" htmlFor="lcr-faq-paste-text">
        <span>Your FAQ text</span>
        <textarea
          id="lcr-faq-paste-text"
          value={text}
          placeholder={"How long is the call?\nTwenty-five minutes.\n\nQ: Is there a cost?\nA: No."}
          onChange={(event) => setText(event.target.value)}
        />
      </label>
      {parsed && (
        <>
          {parsed.rows.length > 0 && (
            <ul className="lcr-faq-paste-rows">
              {parsed.rows.map((row) => (
                <li key={`${row.question}-${row.answer}`}>
                  <span className="lcr-faq-paste-question">{row.question}</span>
                  <span className="lcr-faq-paste-answer">{row.answer}</span>
                  {props.saved.has(row.question) ? (
                    <span className="lcr-chip lcr-chip-green">Saved</span>
                  ) : row.questionTooLong ? (
                    <span className="lcr-chip lcr-chip-amber">
                      Shorten the question below {FAQ_QUESTION_LIMIT} characters
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="lcr-small-button lcr-faq-answer-open"
                      aria-expanded={props.checking === row.question}
                      aria-controls={
                        props.checking === row.question
                          ? "lcr-faq-answers_when_asked-form"
                          : undefined
                      }
                      onClick={() => props.onPicked(row)}
                    >
                      Check and save
                    </button>
                  )}
                  {props.checking === row.question && (
                    <div className="lcr-faq-inline">{props.form}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {parsed.rows.length === 0 && (
            <p className="lcr-settings-note">
              No question and answer could be told apart in this. Put each question on its own line,
              with its answer under it.
            </p>
          )}
          {parsed.unparsed.length > 0 && (
            <p className="lcr-settings-note">
              {parsed.unparsed.length === 1
                ? "One paragraph had no question in it and was left out."
                : `${parsed.unparsed.length} paragraphs had no question in them and were left out.`}
            </p>
          )}
          {parsed.dropped > 0 && (
            <p className="lcr-settings-note">
              Only the first {FAQ_PASTE_ROW_LIMIT} rows are shown. Save these, then paste the rest.
            </p>
          )}
        </>
      )}
    </details>
  );
}

const ageLabel = (confirmedAt: string): string => appDate(confirmedAt);

/**
 * Removing one saved answer, asked for in place rather than in a dialog.
 *
 * The reason is required because the host keeps it with the removal, and a removal nobody can
 * explain later is the audit row this exists to avoid. Nothing is removed until the rep confirms;
 * Escape or "Keep it" closes the panel and leaves the answer exactly as it was.
 */
function RemoveAnswer(props: {
  workspaceId: string;
  entry: Entry;
  onClose: () => void;
  onRemoved: (text: string) => void;
}) {
  const { entry } = props;
  const cache = useQueryClient();
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const remove = useMutation({
    mutationFn: () =>
      send(`${faqPath(props.workspaceId)}/entries/${entry.id}/remove`, { reason: reason.trim() }),
    onSuccess: async () => {
      props.onRemoved(readable(entry.answer));
      await cache.invalidateQueries({ queryKey: ["faq", props.workspaceId] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const idPrefix = `lcr-faq-remove-${entry.id}`;
  return (
    <form
      className="lcr-faq-remove"
      aria-labelledby={`${idPrefix}-title`}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !remove.isPending) {
          event.stopPropagation();
          props.onClose();
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        setMessage("");
        remove.mutate();
      }}
    >
      <p id={`${idPrefix}-title`} className="lcr-faq-remove-title">
        Remove this answer?
      </p>
      <p className="lcr-faq-hint">
        Replies drafted after this stop using it
        {entry.campaignName ? ` in ${entry.campaignName}` : " in every campaign of this workspace"}.
        Drafts already written keep what they say.
      </p>
      <label className="lcr-faq-field" htmlFor={`${idPrefix}-reason`}>
        <span>Why (kept with the removal)</span>
        <input
          ref={input}
          id={`${idPrefix}-reason`}
          type="text"
          maxLength={500}
          value={reason}
          disabled={remove.isPending}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div className="lcr-actions">
        <button
          type="submit"
          className="lcr-small-button lcr-faq-remove-confirm"
          disabled={remove.isPending || reason.trim() === ""}
        >
          {remove.isPending ? "Removing…" : "Remove answer"}
        </button>
        <button
          type="button"
          className="lcr-small-button"
          disabled={remove.isPending}
          onClick={props.onClose}
        >
          Keep it
        </button>
        {message && <span role="alert">{message}</span>}
      </div>
    </form>
  );
}

/**
 * One saved answer: what it says, who stands behind it, and whether it needs the rep again.
 *
 * "Still correct" is offered only when it does something the rep can see a reason for: the answer
 * is old enough to re-read, or it is a campaign's own answer that an answer for every campaign has
 * since overtaken, which is the state where that campaign's replies stop to ask which one applies.
 */
function AnswerRow(props: {
  workspaceId: string;
  entry: Entry;
  /** A newer answer for every campaign, when this campaign's own answer is behind it. */
  overtakenBy: Entry | null;
  editing: boolean;
  form: ReactNode;
  onEdit: () => void;
  /** Whether this row's remove panel is the one open panel on the page. */
  removing: boolean;
  onRemove: () => void;
  onRemoveClosed: () => void;
  onRemoved: (text: string) => void;
}) {
  const { entry, overtakenBy, removing } = props;
  const cache = useQueryClient();
  const [message, setMessage] = useState("");
  const removeButton = useRef<HTMLButtonElement>(null);
  const confirm = useMutation({
    mutationFn: () => send(`${faqPath(props.workspaceId)}/entries/${entry.id}/confirm`, {}),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["faq", props.workspaceId] }),
    onError: (error: Error) => setMessage(error.message),
  });
  const title = entry.question ?? entry.label;
  const labelId = `lcr-faq-row-${entry.id}-label`;
  return (
    <li className="lcr-faq-row">
      <div className="lcr-faq-row-head">
        <span id={labelId} className="lcr-faq-row-label">
          {title}
        </span>
        {entry.campaignName && <span className="lcr-chip">{entry.campaignName} only</span>}
        {entry.ageing && <span className="lcr-chip lcr-chip-amber">Check this is still true</span>}
        {overtakenBy && <span className="lcr-chip lcr-chip-amber">Replies here ask again</span>}
      </div>
      <p className="lcr-faq-row-answer">{readable(entry.answer)}</p>
      {overtakenBy && (
        <p className="lcr-faq-hint">
          The answer for every campaign is newer than this one, so replies in{" "}
          {entry.campaignName ?? "this campaign"} stop to ask which applies. Keep this one if it
          still does, or remove it.
        </p>
      )}
      <div className="lcr-faq-row-foot">
        <span>
          {entry.question !== null && `${entry.label} · `}
          {entry.confirmedByName} · {ageLabel(entry.confirmedAt)}
        </span>
        <span className="lcr-faq-row-actions">
          {(entry.ageing || overtakenBy) && (
            <button
              type="button"
              className="lcr-small-button"
              disabled={confirm.isPending}
              onClick={() => confirm.mutate()}
            >
              {confirm.isPending
                ? "Saving…"
                : overtakenBy
                  ? `Keep for ${entry.campaignName ?? "this campaign"}`
                  : "Still correct"}
            </button>
          )}
          <button
            type="button"
            className="lcr-small-button"
            aria-expanded={props.editing}
            aria-controls={props.editing ? `lcr-faq-${entry.contextKey}-form` : undefined}
            aria-describedby={labelId}
            onClick={props.onEdit}
          >
            Edit
          </button>
          <button
            ref={removeButton}
            type="button"
            className="lcr-small-button lcr-faq-remove-open"
            aria-expanded={removing}
            aria-describedby={labelId}
            onClick={props.onRemove}
          >
            Remove
          </button>
        </span>
      </div>
      {props.editing && <div className="lcr-faq-inline">{props.form}</div>}
      {removing && (
        <RemoveAnswer
          workspaceId={props.workspaceId}
          entry={entry}
          onClose={() => {
            props.onRemoveClosed();
            removeButton.current?.focus();
          }}
          onRemoved={props.onRemoved}
        />
      )}
      {message && <p role="alert">{message}</p>}
    </li>
  );
}

/**
 * The answer for every campaign that a campaign's own answer has fallen behind, or null.
 *
 * The resolver's own rule (`resolveScopes` in context/resolver.ts): per scope the newest answer
 * speaks, and a workspace answer confirmed after the campaign's newest one is a `stale_shadow`, so
 * that campaign's replies stop to ask. Shown on the campaign's row with the one action that settles
 * it, because nothing else on the page would tell the rep why those replies keep asking.
 */
function overtaken(
  entry: Entry,
  entries: readonly Entry[],
  keys: readonly Pick<FactKey, "key" | "campaignOverridable">[],
): Entry | null {
  if (entry.campaignId === null) return null;
  if (!keys.find((key) => key.key === entry.contextKey)?.campaignOverridable) return null;
  const same = entries.filter((other) => other.contextKey === entry.contextKey);
  const newest = (atScope: readonly Entry[]) =>
    atScope.reduce<Entry | null>(
      (best, other) =>
        best === null || Date.parse(other.confirmedAt) >= Date.parse(best.confirmedAt)
          ? other
          : best,
      null,
    );
  const every = newest(same.filter((other) => other.campaignId === null));
  const own = newest(same.filter((other) => other.campaignId === entry.campaignId));
  if (every === null || own === null) return null;
  return Date.parse(every.confirmedAt) > Date.parse(own.confirmedAt) ? every : null;
}

/** Where an answer form was opened from, which is where it renders. */
type Opened = {
  readonly key: string;
  readonly entryId: string | null;
  readonly at: "followup" | "row" | "add" | "paste" | "link";
  readonly campaignId: string | null;
  readonly seed: { readonly question: string; readonly answer: string } | null;
};

/** One draft per form, so opening another fact never throws away what was typed in this one. */
const draftKey = (opened: Opened): string =>
  `${opened.key}\u0000${opened.entryId ?? ""}\u0000${opened.seed?.question ?? ""}\u0000${opened.campaignId ?? ""}`;

/**
 * The saved answers in the order the rep thinks of them: by fact, as the catalogue lists them, and
 * within a fact the answer for every campaign first, then each campaign's own by name.
 */
function ordered(entries: readonly Entry[], keys: readonly FactKey[]): Entry[] {
  const rank = new Map(keys.map((key, index) => [key.key, index]));
  return [...entries].sort(
    (a, b) =>
      (rank.get(a.contextKey) ?? keys.length) - (rank.get(b.contextKey) ?? keys.length) ||
      Number(a.campaignId !== null) - Number(b.campaignId !== null) ||
      (a.campaignName ?? "").localeCompare(b.campaignName ?? "") ||
      a.confirmedAt.localeCompare(b.confirmedAt),
  );
}

/** The page's opening note: what the answers are for. */
function FaqIntro() {
  return (
    <p className="lcr-settings-note">
      What this workspace knows, so a reply does not stop to ask you. An answer reaches every later
      lead in its scope, so keep it general: no names, addresses or anything about one person.
    </p>
  );
}

/** "Your answers" with none saved, saying why it is empty. */
function NoAnswers(props: { answerable: boolean }) {
  return (
    <p className="lcr-settings-note">
      None yet.{" "}
      {props.answerable ? "Answer one of the facts above, or add one below." : "Add one below."}
    </p>
  );
}

/** Where a new answer is started: by choosing a fact, or from a pasted block of FAQ. */
function AddAnswer(props: {
  keys: readonly FactKey[];
  entries: readonly Entry[];
  /** Whether the form below was opened from here, in which case it takes the chooser's place. */
  adding: boolean;
  form: ReactNode;
  pasteSaved: ReadonlySet<string>;
  pasteChecking: string | null;
  pasteForm: ReactNode;
  open: (next: Opened) => void;
}) {
  const { open } = props;
  return (
    <div className="lcr-faq-add">
      <h2>Add an answer</h2>
      {props.adding ? (
        props.form
      ) : (
        <label className="lcr-faq-field" htmlFor="lcr-faq-add-key">
          <span>Fact to answer</span>
          <span className="lcr-select">
            <select
              id="lcr-faq-add-key"
              value=""
              onChange={(event) =>
                event.target.value &&
                open({
                  key: event.target.value,
                  entryId: null,
                  at: "add",
                  campaignId: null,
                  seed: null,
                })
              }
            >
              <option value="">Choose a fact…</option>
              {(["unanswered", "answered"] as const).map((group) => {
                const members = props.keys.filter(
                  (key) =>
                    props.entries.some((entry) => entry.contextKey === key.key) ===
                    (group === "answered"),
                );
                if (members.length === 0) return null;
                return (
                  <optgroup
                    key={group}
                    label={group === "answered" ? "Already answered" : "Not answered yet"}
                  >
                    {members.map((key) => (
                      <option key={key.key} value={key.key}>
                        {key.label}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </span>
        </label>
      )}
      <PasteBlock
        saved={props.pasteSaved}
        checking={props.pasteChecking}
        form={props.pasteForm}
        onPicked={(row) =>
          open({
            key: "answers_when_asked",
            entryId: null,
            at: "paste",
            campaignId: null,
            seed: row,
          })
        }
      />
    </div>
  );
}

/**
 * The workspace's standing answers, as their own page.
 *
 * Reads and writes only the workspace it is given, through routes that name it, and never lists
 * another: the facts one client's campaigns run on are not another client's business.
 */
export function WorkspaceFaq(props: {
  workspaceId: string;
  /**
   * A fact another section asked the rep to answer, with the scope it wants.
   *
   * A campaign held because its emails offer a different meeting length disagrees with a confirmed
   * fact rather than with a setting, and this is where that fact lives. Arriving here opens its
   * form with that campaign chosen, so the way out of the hold is one form rather than a hunt.
   */
  focus?: { key: string; campaignId: string | null } | null;
  onFocused?: () => void;
}) {
  const [opened, setOpened] = useState<Opened | null>(null);
  /**
   * The answer whose remove panel is open. One panel at a time across the page: opening a form
   * closes a remove panel and the reverse, so the rep is never answering and removing at once.
   */
  const [removing, setRemoving] = useState<string | null>(null);
  const drafts = useRef(new Map<string, Draft>());
  /** The control that opened the form, which gets focus back when the form closes. */
  const opener = useRef<HTMLElement | null>(null);
  /** Run once after the next render: moving focus has to wait for the DOM it moves to. */
  const afterRender = useRef<(() => void) | null>(null);
  useEffect(() => {
    const run = afterRender.current;
    afterRender.current = null;
    run?.();
  });
  const [pasteSaved, setPasteSaved] = useState<ReadonlySet<string>>(new Set());
  /** What the last save or removal did, said once in a polite live region. */
  const [status, setStatus] = useState<{ text: ReactNode; focus: boolean } | null>(null);
  const statusLine = useRef<HTMLParagraphElement>(null);
  // A removed row takes its focused button with it; the status line that names it holds focus next.
  useEffect(() => {
    if (status?.focus) statusLine.current?.focus();
  }, [status]);
  const focus = props.focus ?? null;
  const { onFocused } = props;
  useEffect(() => {
    if (focus === null) return;
    opener.current = null;
    setOpened({
      key: focus.key,
      entryId: null,
      at: "link",
      campaignId: focus.campaignId,
      seed: null,
    });
    onFocused?.();
  }, [focus, onFocused]);
  const overview = useQuery({
    queryKey: ["faq", props.workspaceId],
    queryFn: async () => {
      const response = await fetch(faqPath(props.workspaceId), { credentials: "same-origin" });
      if (response.status === 401) throw new Error("Your session ended. Sign in again.");
      if (!response.ok) throw new Error("Standing answers could not be loaded.");
      return OverviewSchema.parse(await response.json());
    },
  });
  const data = overview.data;

  const open = (next: Opened) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Pressing the button of the form that is already open closes it, as Cancel does.
    if (opened && draftKey(opened) === draftKey(next)) {
      close("cancelled");
      return;
    }
    setRemoving(null);
    setOpened(next);
  };
  const close = (outcome: "cancelled" | "saved") => {
    if (!opened) return;
    const closing = opened;
    drafts.current.delete(draftKey(closing));
    const back = opener.current;
    // Back to what opened the form. A save can replace that control (a stop's button becomes
    // "Answered", a pasted row's becomes "Saved"), so focus then stays with the same row, or moves
    // to the next pasted row still to check, never to the top of the document.
    afterRender.current = () => {
      const target =
        (back?.isConnected ? back : null) ??
        document.getElementById(`lcr-faq-fact-${closing.key}`) ??
        (closing.seed ? document.querySelector<HTMLElement>(".lcr-faq-paste-rows button") : null) ??
        document.getElementById("lcr-faq-add-key");
      target?.focus();
    };
    if (outcome === "saved") {
      const label =
        closing.seed?.question ??
        data?.keys.find((key) => key.key === closing.key)?.label ??
        "the answer";
      setStatus({ text: <>Saved “{label}”. Replies drafted from now on use it.</>, focus: false });
      if (closing.seed) setPasteSaved((saved) => new Set([...saved, closing.seed?.question ?? ""]));
    }
    setOpened(null);
  };

  const answerableKeys = new Set(
    (data?.followUp ?? [])
      .filter((fact) => fact.standingImpossible === null)
      .map((fact) => fact.key),
  );
  // A link from another page opens its fact where the rep would have found it: in the list of
  // stops when it is one, otherwise where new answers are added.
  const place =
    opened?.at === "link" ? (answerableKeys.has(opened.key) ? "followup" : "add") : opened?.at;
  const fact = data?.keys.find((key) => key.key === opened?.key) ?? null;
  const existing =
    opened?.entryId && data
      ? (data.entries.find((entry) => entry.id === opened.entryId) ?? null)
      : null;
  const form =
    data && fact && opened ? (
      <AnswerForm
        key={draftKey(opened)}
        workspaceId={props.workspaceId}
        fact={fact}
        campaigns={data.campaigns}
        entries={data.entries}
        existing={existing}
        seed={opened.seed}
        initialCampaignId={opened.campaignId}
        draft={drafts.current.get(draftKey(opened)) ?? null}
        titled={place !== "followup"}
        onDraft={(draft) => drafts.current.set(draftKey(opened), draft)}
        onDone={() => close("saved")}
        onCancel={() => close("cancelled")}
      />
    ) : null;
  const keys = data?.keys ?? [];
  return (
    <section className="lcr-settings-section lcr-faq" aria-labelledby="lcr-faq-title">
      <h1 id="lcr-faq-title">Standing answers</h1>
      <FaqIntro />
      {overview.isPending && <p className="lcr-settings-note">Loading standing answers…</p>}
      {overview.isError && (
        <div className="lcr-faq-load-error">
          <p role="alert">{overview.error.message}</p>
          <button
            type="button"
            className="lcr-small-button"
            disabled={overview.isFetching}
            onClick={() => void overview.refetch()}
          >
            {overview.isFetching ? "Trying…" : "Try again"}
          </button>
        </div>
      )}
      {data && (
        <>
          <FollowUp
            workspaceId={props.workspaceId}
            followUp={data.followUp}
            keys={data.keys}
            openKey={place === "followup" ? (opened?.key ?? null) : null}
            form={place === "followup" ? form : null}
            onAnswer={(key) =>
              open({
                key,
                entryId: null,
                at: "followup",
                campaignId: null,
                seed: null,
              })
            }
          />
          <ProposalQueue workspaceId={props.workspaceId} proposals={data.proposals} />
          <div className="lcr-faq-answers">
            <h2>Your answers</h2>
            <p ref={statusLine} className="lcr-faq-status" role="status" tabIndex={-1}>
              {status?.text}
            </p>
            {data.entries.length === 0 ? (
              <NoAnswers answerable={answerableKeys.size > 0} />
            ) : (
              <ul className="lcr-faq-rows">
                {ordered(data.entries, keys).map((entry) => {
                  const overtakenBy = overtaken(entry, data.entries, keys);
                  return (
                    <AnswerRow
                      key={entry.id}
                      workspaceId={props.workspaceId}
                      entry={entry}
                      overtakenBy={overtakenBy}
                      editing={place === "row" && opened?.entryId === entry.id}
                      form={place === "row" && opened?.entryId === entry.id ? form : null}
                      removing={removing === entry.id}
                      onRemove={() => {
                        if (removing === entry.id) {
                          setRemoving(null);
                          return;
                        }
                        setOpened(null);
                        setRemoving(entry.id);
                      }}
                      onRemoveClosed={() => setRemoving(null)}
                      onEdit={() =>
                        open({
                          key: entry.contextKey,
                          entryId: entry.id,
                          at: "row",
                          campaignId: entry.campaignId,
                          seed: null,
                        })
                      }
                      onRemoved={(text) => {
                        setStatus({
                          text: (
                            <>
                              Removed. Replies drafted from now on will not use <q>{text}</q>
                            </>
                          ),
                          focus: true,
                        });
                        setRemoving(null);
                      }}
                    />
                  );
                })}
              </ul>
            )}
          </div>
          <AddAnswer
            keys={data.keys}
            entries={data.entries}
            adding={place === "add"}
            form={place === "add" ? form : null}
            pasteSaved={pasteSaved}
            pasteChecking={place === "paste" ? (opened?.seed?.question ?? null) : null}
            pasteForm={place === "paste" ? form : null}
            open={open}
          />
        </>
      )}
    </section>
  );
}
